import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";

const API_BASE = "/api";
const STORAGE_KEY = "sentinel_auth";

function getStored() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null;
  } catch { return null; }
}

function setStored(data) {
  if (data) localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  else localStorage.removeItem(STORAGE_KEY);
}

function decodeJwt(token) {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch { return null; }
}

export function useAuth() {
  const [auth, setAuth] = useState(getStored);
  const refreshTimer = useRef(null);
  const refreshTokenRef = useRef(auth?.refresh_token);

  const user = auth?.user || null;
  const token = auth?.access_token || null;
  const isAdmin = user?.role === "admin";
  const isTrader = user?.role === "admin" || user?.role === "trader";

  const doRefresh = useCallback(async (rt) => {
    try {
      const res = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: rt });
      const newAuth = { ...res.data, refresh_token: rt };
      setAuth(newAuth);
      setStored(newAuth);
      refreshTokenRef.current = rt;
      // Schedule next refresh
      const payload = decodeJwt(newAuth.access_token);
      if (payload?.exp) {
        const ms = (payload.exp * 1000) - Date.now() - 120000;
        if (ms > 0) {
          refreshTimer.current = setTimeout(() => doRefresh(rt), ms);
        }
      }
    } catch {
      setAuth(null);
      setStored(null);
    }
  }, []);

  // On mount: schedule refresh for existing token
  useEffect(() => {
    const stored = getStored();
    if (stored?.access_token && stored?.refresh_token) {
      const payload = decodeJwt(stored.access_token);
      if (payload?.exp) {
        const ms = (payload.exp * 1000) - Date.now() - 120000;
        if (ms > 0) {
          refreshTimer.current = setTimeout(() => doRefresh(stored.refresh_token), ms);
        } else {
          doRefresh(stored.refresh_token);
        }
      }
    }
    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, [doRefresh]);

  const login = useCallback(async (username, password) => {
    const res = await axios.post(`${API_BASE}/auth/login`, { username, password });
    const data = res.data;
    setAuth(data);
    setStored(data);
    refreshTokenRef.current = data.refresh_token;
    // Schedule refresh
    const payload = decodeJwt(data.access_token);
    if (payload?.exp) {
      const ms = (payload.exp * 1000) - Date.now() - 120000;
      if (ms > 0) {
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        refreshTimer.current = setTimeout(() => doRefresh(data.refresh_token), ms);
      }
    }
    return data;
  }, [doRefresh]);

  const logout = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    setAuth(null);
    setStored(null);
  }, []);

  const setup = useCallback(async (username, password) => {
    const res = await axios.post(`${API_BASE}/auth/setup`, { username, password });
    return res.data;
  }, []);

  return { user, token, isAdmin, isTrader, login, logout, setup, auth };
}
